import { z } from 'zod';

import { DetectionStatus } from './detection-status.js';
import { IdentityRule } from './identity-rule.js';
import { MotionDirection } from './motion-direction.js';
import { SignalDirection } from './signal-direction.js';
import { SignalType } from './signal-type.js';

const signalTrackSchema = z.object({
  avgLevelDbm: z.number().nullable(),
  avgSnrDb: z.number().nullable(),
  detectionRate: z.number().nullable(),
  levelSlopeDbPerSec: z.number().nullable(),
  snrSlopeDbPerSec: z.number().nullable(),
  suppressionCount: z.number(),
  motionDirection: z.nativeEnum(MotionDirection).nullable(),
  motionDirectionConfidence: z.number().nullable(),
  identityRule: z.nativeEnum(IdentityRule).nullable(),
  identityRuleConfidence: z.number().nullable(),
});

/** Immutable display snapshot behind a synthetic group; `null` for ordinary ones. */
const multiFrequencySuppressionSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  type: z.nativeEnum(SignalType),
  channelFrequencyList: z.array(z.number()),
  bandwidth: z.number(),
  stepStandingTimeMs: z.number(),
  duration: z.number(),
  // From the live `BroadbandSuppression` row, not the snapshot above — optional so a backend
  // that has not yet deployed this field does not degrade the whole snapshot to null.
  startedAt: z.string().nullable().optional(),
  finishedAt: z.string().nullable().optional(),
});

/**
 * Most recent suppression on this group, regardless of kind. `null` when there is nothing
 * re-issuable: no suppression, a synthetic group, or one whose params can't be replayed.
 */
const lastSuppressionSchema = z.object({
  // Not SignalType: the backing column is an unconstrained varchar, and .catch(null) below would
  // silently drop an off-enum (historical/device-auto) row's suppression info.
  type: z.string(),
  duration: z.number().nullable(),
  bandwidth: z.number().nullable(),
  createdAt: z.string(),
});

const detectionGroupDtoSchema = z.object({
  id: z.string(),
  frequency: z.number(),
  signalType: z.nativeEnum(SignalType),
  bandwidth: z.number().optional(),
  offset: z.number(),
  signalLevel: z.number().optional(),
  snr: z.number().optional(),
  patternType: z.string().optional(),
  status: z.nativeEnum(DetectionStatus),
  isManual: z.boolean(),
  detectionsCount: z.number(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  createdAt: z.string(),
  suppressionCount: z.number(),
  suppressionDuration: z.number().nullable().optional(),
  suppressionStartedAt: z.string().nullable().optional(),
  direction: z.nativeEnum(SignalDirection),
  // Optional: a synthetic group has an empty `last_detection_kv`, so the serializer omits both.
  direction_accuracy: z.number().optional(),
  direction_delta: z.number().optional(),
  signalTrack: signalTrackSchema.nullable().optional(),
  parentDetectionGroupId: z.string().nullable().optional(),
  parentDetectionGroupCreatedAt: z.string().nullable().optional(),
  // The KIND discriminator, derived server-side from `source`. Branch on this, not on the snapshot.
  isMultiFrequencySuppression: z.boolean().optional(),
  // Both below: `.catch(null)` degrades the field alone instead of failing the surrounding array,
  // and `.optional()` is load-bearing next to it — without it an ABSENT field fails the inner
  // parse and `.catch(null)` materializes a *present* `null`, losing rolling-deploy tolerance.
  multiFrequencySuppression: multiFrequencySuppressionSchema.nullable().optional().catch(null),
  lastSuppression: lastSuppressionSchema.nullable().optional().catch(null),
});

export const detectionGroupSchema = detectionGroupDtoSchema;
export const signalTrackDtoSchema = signalTrackSchema;
export const multiFrequencySuppressionDtoSchema = multiFrequencySuppressionSchema;
export const lastSuppressionDtoSchema = lastSuppressionSchema;

export const detectionGroupListSchema = z.object({
  groups: z.array(detectionGroupSchema),
});

export type DetectionGroup = z.infer<typeof detectionGroupSchema>;
export type SignalTrack = z.infer<typeof signalTrackSchema>;
export type MultiFrequencySuppression = z.infer<typeof multiFrequencySuppressionSchema>;
export type LastSuppression = z.infer<typeof lastSuppressionSchema>;
